/**
 * EAI-1877 — building the `leadOpportunityUsers` (UserMapping) array for
 * `updateLeadOpportunity`.
 *
 * The mutation document did NOT change: the two new fields live inside the
 * server-side `UserMapping` input type, so only the objects we put in the array
 * change.
 *
 *   isTaskAssignment: true         -> that owner's OPEN tasks move to whoever replaces them
 *   isAppointmentAssignment: true  -> that owner's OPEN appointments move to whoever replaces them
 *
 * Rules enforced here (all from the EAI-1877 spec):
 *  - There is no default and no "apply to all". Work moves ONLY where `true` is
 *    explicitly sent. Omitting a field, sending `false`, or leaving the row out
 *    all mean "do not move".
 *  - A flag is sent only on a row whose owner ACTUALLY changed AND that the user
 *    ticked. Unchanged rows are ignored by the backend anyway, but sending flags
 *    on them implies a choice nobody made.
 *  - We omit rather than send `false` — the backend treats both the same, and
 *    omitting keeps the payload honest about what was chosen.
 *  - Rows with no `id` are NEW assignments, not changes: there is no previous
 *    owner, so there is nothing to hand over.
 *  - Compare as numbers. The form seeds with `+element.userId` but the Select
 *    can hand back a string.
 */

/** Panels a transfer can invalidate, by query OPERATION name. */
export const TRANSFER_QUERIES = {
  TASKS: "getLeadTasks",
  APPOINTMENTS: "getUserAppointment",
};

/** True when this row is an existing assignment whose owner is being replaced. */
export const isOwnerChanged = (row, originalRows) => {
  if (!row?.id) return false; // new assignment — no outgoing owner
  const original = (originalRows || []).find(o => o?.id && +o.id === +row.id);
  return !!original && +original.userId !== +row.userId;
};

/** The existing rows whose owner is being replaced — drives the confirmation UI. */
export const getChangedOwnerRows = (rows, originalRows) =>
  (rows || []).filter(row => isOwnerChanged(row, originalRows));

/**
 * Queries to refetch for what this save transferred. Empty when nothing
 * changed hands or no box was ticked, so the refetch is a no-op.
 */
export const getTransferAffectedQueries = (rows, originalRows) => {
  const changed = getChangedOwnerRows(rows, originalRows);
  const queries = [];
  if (changed.some(row => row.isTaskAssignment)) queries.push(TRANSFER_QUERIES.TASKS);
  if (changed.some(row => row.isAppointmentAssignment)) queries.push(TRANSFER_QUERIES.APPOINTMENTS);
  return queries;
};

/**
 * Map local owner rows to the mutation's UserMapping objects, attaching the
 * transfer flags only where they were both chosen and meaningful.
 *
 * `rows` carry the user's choices as `isTaskAssignment` / `isAppointmentAssignment`
 * (set by the reassign dialog); `originalRows` is the opportunity's stored
 * `leadOpportunityUsers` to diff against.
 */
export const buildLeadOpportunityUsersPayload = (rows, originalRows) =>
  (rows || []).map(row => {
    const base = {
      id: row.id,
      userTypeId: row.userTypeId,
      userId: row.userId,
    };
    if (!isOwnerChanged(row, originalRows)) return base;
    return {
      ...base,
      ...(row.isTaskAssignment ? { isTaskAssignment: true } : {}),
      ...(row.isAppointmentAssignment ? { isAppointmentAssignment: true } : {}),
    };
  });
